import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentRound, getRoster } from '../core/api';
import { useAsync } from '../core/useAsync';
import { useApp } from '../App';
import { spriteUrl } from './spriteCanvas';
import { jukeboxOn, toggleJukebox } from './jukebox';
import { SCENE_SPRITES } from './sceneSprites';
import { TILE, type Dir, type Patrol, type PatrolActor, type Room, type Scene, type Spot, type WorldConfig } from './world';
import type { CurrentRound, RosterMember } from '../core/types';

interface Box {
  title: string;
  text: string;
  actions?: { label: string; alt?: boolean; run: () => void }[];
}

// Remember where the player was standing per world, across navigations (visiting a
// spot leaves the world and comes back — don't dump them at the entrance).
const positions: Record<string, { cur: string; you: { r: number; c: number }; facing: Dir }> = {};

/** The walkable-map engine (spec §19), shared by every RPG style; the WorldConfig
 *  is the only thing that changes between prison and ABC. NPCs are the live roster;
 *  action spots ("!") open the real gameplay routes when the round phase allows;
 *  info spots ("?") are always-open shortcuts to the schedule / table. */
export function RoomEngine({ config }: { config: WorldConfig }) {
  const { rooms, info, floor, palette, npcLines, flavor, npcSlots } = config;
  const nav = useNavigate();
  const { user } = useApp();
  const round = useAsync<CurrentRound | null>(getCurrentRound, []);
  const rosterQ = useAsync<RosterMember[]>(getRoster, []);

  const firstRoom = Object.keys(rooms)[0];
  const saved = positions[config.wrapperClass];
  const [cur, setCur] = useState(() => saved?.cur ?? firstRoom);
  const [you, setYou] = useState<{ r: number; c: number }>(
    () => saved?.you ?? { r: rooms[firstRoom].entry[0], c: rooms[firstRoom].entry[1] },
  );
  const facing = useRef<Dir>(saved?.facing ?? 'd');
  const [box, setBox] = useState<Box | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [flash, setFlash] = useState(false);
  const [music, setMusic] = useState(jukeboxOn());
  const sceneBag = useRef<Record<string, string[]>>({});
  // Moving/animated scenes (spec §34): live positions per patrol, frame counter,
  // line bag, and a tick that forces a re-render each step.
  const patrolLive = useRef<Record<string, Record<string, [number, number]>>>({});
  const frameTick = useRef(0);
  const patrolBag = useRef<Record<string, string[]>>({});
  const pausedRef = useRef(false);
  const [, setPatrolTick] = useState(0);

  useEffect(() => {
    positions[config.wrapperClass] = { cur, you, facing: facing.current };
  }, [cur, you, config.wrapperClass]);

  const r = round.data ?? null;

  // When the admin has turned veto off for the season, the veto spot must vanish
  // from the map entirely — not just close. Drop it from `spots`, then keep its
  // tile walkable and paint it with its room's floor class so it reads as plain
  // floor instead of a wall. Undefined veto_enabled = on, so this is a no-op by
  // default. (Every other world reads these three straight from config.)
  const { spots, cls, walk } = useMemo(() => {
    const base = { spots: config.spots, cls: config.cls, walk: config.walk };
    if (r?.veto_enabled !== false) return base;
    const vc = Object.keys(config.spots).find((k) => config.spots[k].route === '/veto');
    if (!vc) return base;
    const room = Object.keys(rooms).find((rm) => rooms[rm].map.some((row) => row.includes(vc)));
    // If we can't find its room we can't paint it as floor — leave the map alone
    // rather than risk turning the veto tile into a wall.
    if (!room) return base;
    return {
      spots: Object.fromEntries(Object.entries(config.spots).filter(([k]) => k !== vc)),
      cls: { ...config.cls, [vc]: floor[room] },
      walk: new Set([...config.walk, vc]),
    };
  }, [r?.veto_enabled, rooms, floor]);

  const slotPlayer = useMemo(() => {
    const others = (rosterQ.data ?? []).filter((m) => m.user_id !== user.user_id);
    const map: Record<string, RosterMember> = {};
    npcSlots.forEach((slot, i) => {
      if (others[i]) map[slot] = others[i];
    });
    return map;
  }, [rosterQ.data, user.user_id, npcSlots]);

  const spotOpen = (s: Spot) => !!r && r.phase === s.phase;
  const spotTodo = (s: Spot) => spotOpen(s) && !(s.doneField && r?.you?.[s.doneField]);

  // Static scenes (spec §27): archetype tableaux, shown only when the round is in
  // the required state. They override the roster NPC at their tiles while active.
  const activeScenes = (): Scene[] =>
    (config.scenes ?? []).filter((sc) => {
      if (sc.room !== cur) return false;
      if (sc.requires === 'voted') return r?.phase === 'VOTING';
      if (sc.requires === 'unvoted') return r?.phase === 'SUBMITTING';
      return true;
    });
  const sceneActorAt = (pos: string): { scene: Scene; key: string } | null => {
    for (const sc of activeScenes()) if (sc.actors[pos]) return { scene: sc, key: sc.actors[pos] };
    return null;
  };
  const sceneLine = (sc: Scene): string => {
    const bag = sceneBag.current;
    if (!bag[sc.id] || !bag[sc.id].length) bag[sc.id] = sc.lines.slice().reverse();
    return bag[sc.id].pop()!;
  };

  // Moving/animated scenes (spec §34). 'voted'/'served' -> VOTING; movers roam an
  // area, holds are pinned. Roles not paths: flee gains distance, chase closes it
  // but never lands on the target (catching him ends the joke).
  pausedRef.current = box !== null || menuOpen;
  const activePatrols = (): Patrol[] =>
    (config.patrols ?? []).filter((p) => {
      if (p.room !== cur) return false;
      if (p.requires === 'voted' || p.requires === 'served') return r?.phase === 'VOTING';
      if (p.requires === 'unvoted') return r?.phase === 'SUBMITTING';
      return true;
    });
  const inArea = (p: Patrol, rr: number, cc: number): boolean =>
    !!p.area &&
    rr >= p.area.r[0] && rr <= p.area.r[1] && cc >= p.area.c[0] && cc <= p.area.c[1] &&
    walk.has(rooms[p.room].map[rr][cc]);
  const initPatrol = (p: Patrol) => {
    const live = patrolLive.current;
    if (live[p.id]) return;
    live[p.id] = {};
    if (p.cast.every((a) => a.role === 'hold')) {
      p.cast.forEach((a) => (live[p.id][a.k] = a.at as [number, number]));
      return;
    }
    const cells: [number, number][] = [];
    if (p.area)
      for (let rr = p.area.r[0]; rr <= p.area.r[1]; rr++)
        for (let cc = p.area.c[0]; cc <= p.area.c[1]; cc++) if (inArea(p, rr, cc)) cells.push([rr, cc]);
    p.cast.forEach((a, i) => (live[p.id][a.k] = a.at ?? cells[Math.floor((i * cells.length) / p.cast.length)]));
  };
  const dist = (a: [number, number], b: [number, number]) => Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]);
  const stepPatrol = (p: Patrol) => {
    initPatrol(p);
    const pos = patrolLive.current[p.id];
    p.cast.forEach((a) => {
      if (a.role === 'hold') return;
      const here = pos[a.k];
      const opts = ([[0, 1], [0, -1], [1, 0], [-1, 0]] as [number, number][])
        .map(([dr, dc]) => [here[0] + dr, here[1] + dc] as [number, number])
        .filter(([rr, cc]) => inArea(p, rr, cc));
      if (!opts.length) return;
      let pick: [number, number];
      if (a.role === 'flee') {
        const away = pos[a.from as string];
        const best = Math.max(...opts.map((o) => dist(o, away)));
        const good = opts.filter((o) => dist(o, away) >= best - 1);
        pick = good[Math.floor(Math.random() * good.length)];
      } else {
        const t = pos[a.target as string];
        const best = Math.min(...opts.map((o) => dist(o, t)));
        const good = opts.filter((o) => dist(o, t) === best);
        pick = good[Math.floor(Math.random() * good.length)];
        if (pick[0] === t[0] && pick[1] === t[1]) pick = here; // never catch him
      }
      pos[a.k] = pick;
    });
  };
  const patrolPos = (p: Patrol, a: PatrolActor): [number, number] => {
    initPatrol(p);
    return patrolLive.current[p.id][a.k];
  };
  const patrolAt = (rr: number, cc: number): { patrol: Patrol; actor: PatrolActor } | null => {
    for (const p of activePatrols())
      for (const a of p.cast) {
        const [pr, pc] = patrolPos(p, a);
        if (pr === rr && pc === cc) return { patrol: p, actor: a };
      }
    return null;
  };
  const patrolLine = (p: Patrol): string => {
    const bag = patrolBag.current;
    if (!bag[p.id] || !bag[p.id].length) bag[p.id] = p.lines.slice().reverse();
    return bag[p.id].pop()!;
  };

  const R: Room = rooms[cur];

  function walkable(nr: number, nc: number): boolean {
    if (nr < 0 || nr >= R.map.length || nc < 0 || nc >= R.map[0].length) return false;
    if (R.npc[`${nr},${nc}`] && slotPlayer[R.npc[`${nr},${nc}`]]) return false;
    if (sceneActorAt(`${nr},${nc}`)) return false; // static scene actors block
    const hp = patrolAt(nr, nc);
    if (hp && hp.actor.role === 'hold') return false; // holds block; movers don't
    const ch = R.map[nr][nc];
    return walk.has(ch) || /[1-4]/.test(ch) || spots[ch] !== undefined;
  }

  function goExit(key: string) {
    const x = R.exits[key];
    if (!x) return;
    if (x.route) return void nav(x.route);
    setFlash(true);
    setBox(null);
    window.setTimeout(() => {
      setCur(x.to!);
      setYou({ r: x.r!, c: x.c! });
      if (x.face2) facing.current = x.face2;
      setFlash(false);
    }, 120);
  }

  function move(d: Dir) {
    if (menuOpen || box) return;
    facing.current = d;
    const nr = you.r + (d === 'u' ? -1 : d === 'd' ? 1 : 0);
    const nc = you.c + (d === 'l' ? -1 : d === 'r' ? 1 : 0);
    if (!walkable(nr, nc)) {
      setYou((p) => ({ ...p })); // re-render so the chevron reflects facing
      return;
    }
    const ch = R.map[nr][nc];
    if (/[1-4]/.test(ch)) return goExit(ch);
    setYou({ r: nr, c: nc });
    if (spots[ch]) openSpot(ch);
  }

  function openSpot(k: string) {
    const s = spots[k];
    if (!spotOpen(s)) return void setBox({ title: s.name, text: s.shut });
    setBox({
      title: s.name,
      text: s.sub,
      actions: [
        { label: 'GO ON', run: () => nav(s.route) },
        { label: 'LATER', alt: true, run: () => setBox(null) },
      ],
    });
  }

  function openInfo(k: string) {
    const s = info[k];
    setBox({
      title: s.name,
      text: s.sub,
      actions: [
        { label: 'LOOK', run: () => nav(s.route) },
        { label: 'LATER', alt: true, run: () => setBox(null) },
      ],
    });
  }

  function interact() {
    if (box) return void setBox(null);
    const d = facing.current;
    const tr = you.r + (d === 'u' ? -1 : d === 'd' ? 1 : 0);
    const tc = you.c + (d === 'l' ? -1 : d === 'r' ? 1 : 0);
    const pat = patrolAt(tr, tc);
    if (pat) return void setBox({ title: R.name, text: patrolLine(pat.patrol) });
    const sc = sceneActorAt(`${tr},${tc}`);
    if (sc) return void setBox({ title: R.name, text: sceneLine(sc.scene) });
    const slot = R.npc[`${tr},${tc}`];
    const who = slot && slotPlayer[slot];
    if (who) {
      const line = npcLines[(who.user_id + you.r) % npcLines.length];
      return void setBox({ title: who.display_name.toUpperCase(), text: line });
    }
    const ch = R.map[tr] && R.map[tr][tc];
    if (ch && spots[ch]) return openSpot(ch);
    if (ch && info[ch]) return openInfo(ch);
    if (ch && flavor[ch]) return void setBox(flavor[ch]);
    if (ch && /[1-4]/.test(ch)) {
      const x = R.exits[ch];
      return void setBox({
        title: x.name,
        text: 'Head on through.',
        actions: [
          { label: 'GO', run: () => goExit(ch) },
          { label: 'STAY', alt: true, run: () => setBox(null) },
        ],
      });
    }
    setBox({ title: R.name, text: R.blurb });
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const m: Record<string, Dir> = { ArrowUp: 'u', ArrowDown: 'd', ArrowLeft: 'l', ArrowRight: 'r' };
      if (m[e.key]) {
        e.preventDefault();
        move(m[e.key]);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        interact();
      } else if (e.key === 'Escape') {
        setMenuOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur, you, box, menuOpen, slotPlayer, r]);

  // One patrol timer per room (min ms of the active patrols), paused while a box or
  // menu is open, and stopped entirely by reduced-motion (actors still render).
  useEffect(() => {
    const patrols = (config.patrols ?? []).filter((p) => {
      if (p.room !== cur) return false;
      if (p.requires === 'voted' || p.requires === 'served') return r?.phase === 'VOTING';
      if (p.requires === 'unvoted') return r?.phase === 'SUBMITTING';
      return true;
    });
    if (!patrols.length) return;
    patrols.forEach(initPatrol);
    setPatrolTick((t) => t + 1); // place them
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ms = Math.min(...patrols.map((p) => p.ms));
    const id = window.setInterval(() => {
      if (pausedRef.current) return;
      frameTick.current++;
      patrols.forEach(stepPatrol);
      setPatrolTick((t) => t + 1);
    }, ms);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur, r?.phase]);

  const tally = r
    ? r.phase === 'VOTING'
      ? { a: r.ballots_in, b: r.expected_players }
      : { a: r.submissions_in, b: r.expected_players }
    : null;

  const glyphFor = (row: string, rr: number, cc: number, ch: string) =>
    !(row[cc - 1] === ch) && !(R.map[rr - 1] && R.map[rr - 1][cc] === ch);

  return (
    <div className={config.wrapperClass}>
      <header className="hud">
        <div className="hud-top">
          <span className="hud-room">{R.name}</span>
          <span className="hud-t">{r ? r.theme.toUpperCase() : round.loading ? 'LOADING…' : 'NO ROUND'}</span>
        </div>
        <div className="hud-b">
          <span className="floors">
            {Object.entries(rooms).map(([k, room]) => (
              <i key={k} className={k === cur ? 'here' : ''}>{room.floor}</i>
            ))}
          </span>
          {tally ? (
            <button
              className="tally"
              onClick={() => nav('/progress')}
              aria-label={`voting progress ${tally.a} of ${tally.b}`}
              title="Voting progress"
            >
              <span className="tcount">{tally.a}/{tally.b}</span>
              {Array.from({ length: tally.b }, (_, i) => (
                <i key={i} className={i < tally.a ? 'in' : ''} />
              ))}
            </button>
          ) : null}
        </div>
      </header>

      <div className="view">
        <div className="room" style={{ width: R.map[0].length * TILE, height: R.map.length * TILE }}>
          {R.map.flatMap((row, rr) =>
            [...row].map((ch, cc) => {
              const isExit = /[1-4]/.test(ch);
              const isSpot = spots[ch] !== undefined;
              const isInfo = info[ch] !== undefined;
              let klass = 'tile ';
              if (isExit) {
                const x = R.exits[ch];
                klass += x.kind === 'stair' ? `stair${x.face === 'l' || x.face === 'r' ? ' side' : ''}` : `door d${x.face}`;
              } else if (isSpot) {
                klass += `${floor[cur]} spot${spotOpen(spots[ch]) ? '' : ' shut'}`;
              } else if (isInfo) {
                klass += 'board';
              } else {
                klass += cls[ch] || 'w';
              }
              return (
                <div key={`${rr}-${cc}`} className={klass} style={{ left: cc * TILE, top: rr * TILE }}>
                  {isExit && glyphFor(row, rr, cc, ch) ? <span className="glyph">{R.exits[ch].glyph}</span> : null}
                  {isSpot && spotTodo(spots[ch]) ? <span className="mark">!</span> : null}
                  {isInfo ? <span className="mark info">?</span> : null}
                </div>
              );
            }),
          )}

          {Object.entries(R.npc).map(([pos, slot]) => {
            const who = slotPlayer[slot];
            if (!who) return null;
            if (sceneActorAt(pos)) return null; // an active scene takes this tile
            const [pr, pc] = pos.split(',').map(Number);
            return (
              <div
                key={pos}
                className="ent"
                title={who.display_name}
                style={{ left: pc * TILE, top: pr * TILE, backgroundImage: `url(${spriteUrl(who.sprite_data, `n${who.user_id}`, false, palette)})` }}
              />
            );
          })}

          {/* static scene actors (archetype tableaux) */}
          {activeScenes().flatMap((scn) =>
            Object.entries(scn.actors).map(([pos, key]) => {
              const [pr, pc] = pos.split(',').map(Number);
              return (
                <div
                  key={`sc-${scn.id}-${pos}`}
                  className="ent"
                  style={{ left: pc * TILE, top: pr * TILE, backgroundImage: `url(${spriteUrl(SCENE_SPRITES[key], `scene-${key}`, false, palette)})` }}
                />
              );
            }),
          )}

          {/* moving / animated scene actors */}
          {activePatrols().flatMap((p) =>
            p.cast.map((a) => {
              const [pr, pc] = patrolPos(p, a);
              const key = a.frames ? a.frames[frameTick.current % a.frames.length] : a.k;
              return (
                <div
                  key={`pt-${p.id}-${a.k}`}
                  className="ent"
                  style={{ left: pc * TILE, top: pr * TILE, backgroundImage: `url(${spriteUrl(SCENE_SPRITES[key], `scene-${key}`, false, palette)})` }}
                >
                  {a.bubble ? <div className="bubble" style={{ left: -4, top: -22 }}>{a.bubble}</div> : null}
                </div>
              );
            }),
          )}

          <div className="shadow beam" style={{ left: you.c * TILE, top: you.r * TILE }} />
          <div
            className="ent me"
            style={{ left: you.c * TILE, top: you.r * TILE, backgroundImage: `url(${spriteUrl(null, 'me', true, palette)})` }}
          />
        </div>

        <div className={`flash${flash ? ' go' : ''}`} />

        {box ? (
          <div className="box show">
            <div className="box-t">{box.title}</div>
            <div className="box-s">{box.text}</div>
            <div className="box-a">
              {(box.actions ?? [{ label: 'OK', alt: true, run: () => setBox(null) }]).map((a, i) => (
                <button key={i} className={a.alt ? 'alt' : ''} onClick={a.run}>{a.label}</button>
              ))}
            </div>
          </div>
        ) : null}

        {menuOpen ? (
          <div className="menu show" onClick={(e) => e.target === e.currentTarget && setMenuOpen(false)}>
            <div className="menu-in">
              <h2>{config.menuTitle}</h2>
              {Object.entries(rooms).map(([k, room]) => (
                <button key={k} onClick={() => { setMenuOpen(false); setCur(k); setYou({ r: room.entry[0], c: room.entry[1] }); facing.current = 'd'; }}>
                  {room.name}
                </button>
              ))}
              {config.menuLinks.map((lnk) => (
                <button key={lnk.route} className={lnk.ex ? 'ex' : ''} onClick={() => nav(lnk.route)}>
                  {lnk.label}<span className="sub">{lnk.sub}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className="pad">
        <div className="dpad">
          <button className="sp" />
          <button onClick={() => move('u')} aria-label="up">▲</button>
          <button className="sp" />
          <button onClick={() => move('l')} aria-label="left">◀</button>
          <button className="sp" />
          <button onClick={() => move('r')} aria-label="right">▶</button>
          <button className="sp" />
          <button onClick={() => move('d')} aria-label="down">▼</button>
          <button className="sp" />
        </div>
        <div className="btns">
          <button
            aria-label={music ? 'music off' : 'music on'}
            style={{ opacity: music ? 1 : 0.5 }}
            onClick={() => setMusic(toggleJukebox(config.jukebox))}
          >
            ♪
          </button>
          <button onClick={interact}>A</button>
          <button onClick={() => setMenuOpen((o) => !o)}>START</button>
        </div>
      </div>
    </div>
  );
}
